import type {
  AddComponentBody,
  ApproveBody,
  ComponentConfig,
  ComponentsResponse,
  ConfigHistoryEntry,
  ConfigSaveBody,
  ConflictBody,
  IncidentActionResult,
  IncidentConflictBody,
  IncidentPublishFailedBody,
  IncidentUpdateBody,
  IncidentsResponse,
  ManualProposalBody,
  Me,
  OpenIncidentBody,
  Proposal,
  ProposalDetail,
  PublishFailedBody,
  PublishResult,
  RedecideBody,
  RejectBody,
  ResolveIncidentBody,
  RetireBody,
  RetireResult,
  RetryBody,
  RevertBody,
  RunHistoryPage,
  Scope,
  UnprocessableBody,
} from '@/domain/types';

/**
 * The console API (TDD §11.2), as one interface. `HttpConsoleApi` speaks to the API lambda;
 * `MockConsoleApi` is a stateful in-memory stand-in used until the lambda exists and in tests.
 * Screens only ever see this interface.
 */
export interface ConsoleApi {
  /** Who the API takes the caller to be (CHG-050). */
  me(): Promise<Me>;
  getComponents(scope: Scope): Promise<ComponentsResponse>;
  getRuns(scope: Scope, capabilityId: string, opts?: RunsQuery): Promise<RunHistoryPage>;
  getQueue(scope: Scope): Promise<Proposal[]>;
  getProposal(proposalId: string): Promise<ProposalDetail>;
  getIncidents(scope: Scope): Promise<IncidentsResponse>;

  approve(proposalId: string, body: ApproveBody): Promise<PublishResult>;
  reject(proposalId: string, body: RejectBody): Promise<{ proposal: Proposal }>;
  redecide(proposalId: string, body: RedecideBody): Promise<PublishResult>;
  retry(proposalId: string, body: RetryBody): Promise<PublishResult>;
  createManualProposal(body: ManualProposalBody): Promise<PublishResult>;

  openIncident(scope: Scope, body: OpenIncidentBody): Promise<IncidentActionResult>;
  postIncidentUpdate(
    scope: Scope,
    incidentId: string,
    body: IncidentUpdateBody,
  ): Promise<IncidentActionResult>;
  resolveIncident(
    scope: Scope,
    incidentId: string,
    body: ResolveIncidentBody,
  ): Promise<IncidentActionResult>;

  getConfig(scope: Scope, capabilityId: string): Promise<ComponentConfig>;
  saveConfig(
    scope: Scope,
    capabilityId: string,
    body: ConfigSaveBody,
  ): Promise<{ config: ComponentConfig }>;
  addComponent(scope: Scope, body: AddComponentBody): Promise<{ config: ComponentConfig }>;
  getConfigHistory(scope: Scope, capabilityId: string): Promise<ConfigHistoryEntry[]>;
  revertConfig(
    scope: Scope,
    capabilityId: string,
    body: RevertBody,
  ): Promise<{ config: ComponentConfig }>;
  /** Retire a component for good (CHG-057): there is no way back, and its id cannot be added again. */
  retireComponent(scope: Scope, capabilityId: string, body: RetireBody): Promise<RetireResult>;
}

/**
 * What the HTTP client needs from sign-in (CHG-050): the access token to send, a way to get a
 * fresh one after a 401, and a way to start signing in again when there is none. Without one the
 * client sends no Authorization header.
 */
export interface TokenSource {
  accessToken(): Promise<string | null>;
  /** A silent renewal; null when it cannot be had. */
  renewAccessToken(): Promise<string | null>;
  signInAgain(): void;
  /** A request the API accepted: the sign-in is working, so a later 401 may start one again. */
  authorized(): void;
}

export interface RunsQuery {
  limit?: number;
  before?: string;
}

/**
 * Every non-2xx response becomes an ApiError. The three bodies the TDD specifies (409, 422,
 * 502) are exposed through typed accessors so screens can show the current state, the
 * violations, or the failed proposal without re-parsing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? describe(status, body));
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get conflict(): ConflictBody | null {
    return this.status === 409 && isRecord(this.body) && 'current' in this.body
      ? (this.body as unknown as ConflictBody)
      : null;
  }

  get unprocessable(): UnprocessableBody | null {
    return this.status === 422 && isRecord(this.body) && 'error' in this.body
      ? (this.body as unknown as UnprocessableBody)
      : null;
  }

  get publishFailed(): PublishFailedBody | null {
    return this.status === 502 && isRecord(this.body) && 'proposal' in this.body
      ? (this.body as unknown as PublishFailedBody)
      : null;
  }

  /** 409 from an incident action (CHG-055); a proposal's version conflict is `conflict`. */
  get incidentConflict(): IncidentConflictBody | null {
    return this.status === 409 && isRecord(this.body) && this.body.error === 'incident_conflict'
      ? (this.body as unknown as IncidentConflictBody)
      : null;
  }

  /** 502 from an incident action (CHG-055): carries an `outcome` and no proposal. */
  get incidentPublishFailed(): IncidentPublishFailedBody | null {
    return this.status === 502 &&
      isRecord(this.body) &&
      this.body.error === 'publish_failed' &&
      'outcome' in this.body &&
      !('proposal' in this.body)
      ? (this.body as unknown as IncidentPublishFailedBody)
      : null;
  }

  /** Field-level messages from a 422, keyed by field, for inline form errors. */
  get violations(): Record<string, string> {
    const list = this.unprocessable?.violations ?? [];
    return Object.fromEntries(list.map((v) => [v.field, v.message]));
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function describe(status: number, body: unknown): string {
  // The role table and the token decide these two; the body's words are for logs, not people.
  if (status === 403) return "You don't have access to do this.";
  if (status === 401) return 'Your session has ended. Sign in again.';
  if (isRecord(body)) {
    if (typeof body.detail === 'string') return body.detail;
    if (typeof body.last_error === 'string') return `Publish failed: ${body.last_error}`;
    if (typeof body.error === 'string') return body.error.replaceAll('_', ' ');
  }
  switch (status) {
    case 409:
      return 'Someone or something changed this since you loaded it.';
    case 422:
      return 'The action is not allowed in the current state.';
    case 502:
      return 'The publish failed after three tries.';
    default:
      return `The console API answered ${status}.`;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
